import { Currency } from '../models/bank-account/bank-account';

/**
 * Plausible bounds for a hand-typed exchange rate (#5135).
 *
 * **A mirror, not the authority.** The real list is `Finance:ExchangeRateBounds` in the API's configuration
 * and `ExchangeRateBoundsPolicy` enforces it — this copy exists so the field can refuse a slip as it is
 * typed rather than on save. If the two disagree the API wins, and the worst outcome is a warning this
 * shows early or a rejection it did not predict.
 *
 * **Quoted on the USD pivot** — units of the currency per 1 USD, the way a person says it out loud
 * ("twenty-four fifty to the dollar") and the way `ApDocument.rateToUsd` stores it. NOT the
 * currency → functional multiplier the GL posts with, which is the reciprocal and where 0.04 is normal.
 *
 * **A typo guard, not a market check.** Miss a decimal on 24.50 and the document books at 2.45: the total
 * still looks plausible, the invoice reads Posted, and the payable is out by a factor of ten with nothing
 * on the document to show it. A currency absent from this table is simply unbounded — inventing a range for
 * one nobody trades here would block a real rate to prevent a hypothetical typo.
 */
export const EXCHANGE_RATE_BOUNDS: Partial<Record<Currency, { min: number; max: number }>> = {
  [Currency.HNL]: { min: 20, max: 30 },
  [Currency.EUR]: { min: 0.7, max: 1.1 },
};

/** The bounds for a currency, or null where none are known (and so nothing is enforced). */
export function exchangeRateBoundsFor(currency: Currency | null | undefined): { min: number; max: number } | null {
  return currency == null ? null : (EXCHANGE_RATE_BOUNDS[currency] ?? null);
}

/** True when a typed rate is one this currency plausibly trades at. A blank rate passes — not typing one
 *  means "use the ordinary sources", which is the normal case. */
export function isPlausibleExchangeRate(currency: Currency | null | undefined, rateToUsd: number | null | undefined): boolean {
  if (rateToUsd == null) return true;
  if (rateToUsd <= 0) return false;
  const bounds = exchangeRateBoundsFor(currency);
  return !bounds || (rateToUsd >= bounds.min && rateToUsd <= bounds.max);
}
